import axios from 'axios';
import {
  getEnvironment,
  parseReviewEnvironmentHost,
  getApiHost,
  ENVIRONMENTS,
  HOSTS_PER_ENV,
} from '@hotjar/common';

const reviewEnvUrlSuffix = parseReviewEnvironmentHost()?.reviewUrlSuffix;

// Settings for the API and client script URLs
const SETTINGS = Object.freeze({
  [ENVIRONMENTS.LIVE]: {
    apiHost: HOSTS_PER_ENV[ENVIRONMENTS.LIVE].SURVEYS,
    clientScriptHost: 'static.hotjar.com',
  },
  [ENVIRONMENTS.REVIEW_WEBAPP]: {
    apiHost: 'surveys-integration.live.eks.hotjar.com',
    clientScriptHost: `static-integration.live.eks.hotjar.com`,
  },
  [ENVIRONMENTS.REVIEW]: {
    apiHost: `surveys-${reviewEnvUrlSuffix}`,
    clientScriptHost: `static-${reviewEnvUrlSuffix}`,
  },
  [ENVIRONMENTS.STAGING]: {
    apiHost: HOSTS_PER_ENV[ENVIRONMENTS.STAGING].SURVEYS,
    clientScriptHost: 'static-staging.hotjar.com',
  },
  [ENVIRONMENTS.DEV]: {
    apiHost: getApiHost(false),
    clientScriptHost: 'static.local.hotjar.com:8443',
  },
  [ENVIRONMENTS.DEV_OLD]: {
    apiHost: getApiHost(false),
    clientScriptHost: 'static.local.hotjar.com:8443',
  },
});

// Key names match keys in hjSettings as expected by the client script. Don't change them!
const SCRIPT_SETTINGS = Object.freeze({
  // We don't want to override any settings on live
  [ENVIRONMENTS.LIVE]: {
    environment: 'live',
  },
  [ENVIRONMENTS.REVIEW_WEBAPP]: {
    environment: 'integration',
    host: `in-integration.live.eks.hotjar.com`,
    identifyEndpoint: 'https://identify.ew1-integration-1.hotjarians.net',
    insightsHost: HOSTS_PER_ENV[ENVIRONMENTS.REVIEW_WEBAPP].INSIGHTS,
    insightsApiHost: HOSTS_PER_ENV[ENVIRONMENTS.REVIEW_WEBAPP].INSIGHTS,
    staticHost: `static-integration.live.eks.hotjar.com`,
    varsHost: `vars-integration.live.eks.hotjar.com`,
    viewCounterEndpoint: 'https://vc-staging.hotjar.io/sessions',
    viewCounterHitPercentage: 1.0,
    surveyImpressionsEndpoint: 'https://surveystats-staging.cs.hotjar.io/hit',
    wsHost: `ws1-integration.live.eks.hotjar.com`,
    metricsUrl: 'https://pacman-metrics-integration.live.eks.hotjar.com/',
    askUrl: 'https://in-ask-backend-staging.eu-west-1.cs.hotjar.com/',
  },
  [ENVIRONMENTS.REVIEW]: {
    environment: 'integration',
    host: `in-${reviewEnvUrlSuffix}`,
    identifyEndpoint: 'https://identify.ew1-integration-1.hotjarians.net',
    insightsHost: HOSTS_PER_ENV[ENVIRONMENTS.REVIEW].INSIGHTS,
    insightsApiHost: HOSTS_PER_ENV[ENVIRONMENTS.REVIEW].INSIGHTS,
    staticHost: `static-${reviewEnvUrlSuffix}`,
    varsHost: `vars-${reviewEnvUrlSuffix}`,
    viewCounterEndpoint: 'https://vc-staging.hotjar.io/sessions',
    viewCounterHitPercentage: 1.0,
    surveyImpressionsEndpoint: 'https://surveystats-staging.cs.hotjar.io/hit',
    wsHost: `ws1-${reviewEnvUrlSuffix}`,
    metricsUrl: 'https://pacman-metrics-integration.live.eks.hotjar.com/',
    askUrl: 'https://in-ask-backend-staging.eu-west-1.cs.hotjar.com/',
  },
  [ENVIRONMENTS.STAGING]: {
    environment: 'staging',
    host: 'in-staging.hotjar.com',
    identifyEndpoint: 'https://identify.ew1-integration-1.hotjarians.net',
    insightsHost: HOSTS_PER_ENV[ENVIRONMENTS.STAGING].INSIGHTS,
    insightsApiHost: HOSTS_PER_ENV[ENVIRONMENTS.STAGING].INSIGHTS,
    staticHost: 'static-staging.hotjar.com',
    varsHost: 'vars-staging.hotjar.com',
    viewCounterEndpoint: 'https://vc-staging.hotjar.io/sessions',
    viewCounterHitPercentage: 1.0,
    surveyImpressionsEndpoint: 'https://surveystats-staging.cs.hotjar.io/hit',
    wsHost: 'ws1-staging.hotjar.com',
    metricsUrl: 'https://pacman-metrics-integration.live.eks.hotjar.com/',
    askUrl: 'https://in-ask-backend-staging.eu-west-1.cs.hotjar.com/',
  },
  [ENVIRONMENTS.DEV]: {
    environment: 'dev',
    host: 'in.local.hotjar.com:8443',
    identifyEndpoint: 'http://localhost:8000',
    insightsHost: HOSTS_PER_ENV[ENVIRONMENTS.DEV].INSIGHTS,
    insightsApiHost: HOSTS_PER_ENV[ENVIRONMENTS.DEV].INSIGHTS,
    staticHost: 'static.local.hotjar.com:8443',
    varsHost: 'vars.local.hotjar.com:9443',
    viewCounterEndpoint: null, // No view counting
    surveyImpressionsEndpoint: null, // No impression counting
    wsHost: 'ws.local.hotjar.com:8443',
    metricsUrl: 'https://pacman-metrics-integration.live.eks.hotjar.com/',
    // TODO: Set this to the proper local host for Ask service when it's defined
    askUrl: HOSTS_PER_ENV[ENVIRONMENTS.DEV].INSIGHTS,
  },
  [ENVIRONMENTS.DEV_OLD]: {
    environment: 'dev',
    host: 'in.local.hotjar.com:8443',
    identifyEndpoint: 'http://localhost:8000',
    insightsHost: HOSTS_PER_ENV[ENVIRONMENTS.DEV_OLD].INSIGHTS,
    insightsApiHost: HOSTS_PER_ENV[ENVIRONMENTS.DEV_OLD].INSIGHTS,
    staticHost: 'static.local.hotjar.com:8443',
    varsHost: 'vars.local.hotjar.com:9443',
    viewCounterEndpoint: null, // No view counting
    surveyImpressionsEndpoint: null, // No impression counting
    wsHost: 'ws.local.hotjar.com:8443',
    metricsUrl: 'https://pacman-metrics-integration.live.eks.hotjar.com/',
  },
});

function getRedirectHost(environment) {
  return environment === ENVIRONMENTS.LIVE
    ? 'surveys.contentsquare.com'
    : 'surveys-staging.contentsquare.com';
}

function renderClosedSurvey(container, showBranding = true) {
  // eslint-disable-next-line no-param-reassign
  container.innerHTML = '';
  const errorContainer = document.createElement('div');
  errorContainer.classList.add('error-container');

  const heading = document.createElement('h3');
  const headingText = document.createTextNode('Nothing to see here');
  heading.appendChild(headingText);

  const paragraph = document.createElement('p');
  const paragraphText = document.createTextNode(
    'It looks like this survey stopped collecting responses or was removed. Maybe both!',
  );
  paragraph.appendChild(paragraphText);

  container.classList.add('_hj-survey-404');
  errorContainer.appendChild(heading);
  errorContainer.appendChild(paragraph);

  if (showBranding) {
    const brandingLink = document.createElement('a');
    const brandingLinkText = document.createTextNode('Try Surveys now');
    brandingLink.appendChild(brandingLinkText);
    brandingLink.classList.add('btn');
    brandingLink.href =
      'https://www.hotjar.com/feedback-surveys/?utm_source=external_survey_closed';
    brandingLink.target = '_blank';

    errorContainer.appendChild(brandingLink);
    container.classList.add('_hj-show-brand');
  }

  container.appendChild(errorContainer);
}

export function renderCallback(props) {
  document.body.style.backgroundColor = props.background;
}

function registerSurveyEventHandler(notify, signal) {
  window.addEventListener(
    'hotjar-survey-event',
    (e) => {
      const messages = {
        surveyClosed: 'close',
        surveyDisplayed: 'open',
        surveyCompleted: 'survey-finished',
        surveyCompletedError: 'survey-finished',
      };

      const message = messages[e.detail.event];
      if (!message) return;
      notify(message);
    },
    { signal },
  );
}

function getNotifyBridge() {
  const sendMessage = (message) => {
    window.CSSurveysJavascriptBridge.postMessage(JSON.stringify({ message }));
  };

  let bridge = window.CSSurveysJavascriptBridge;
  const messageQueue = [];
  // monitor the CSSurveysJavascriptBridge property so we can send messages when it is defined
  Object.defineProperty(window, 'CSSurveysJavascriptBridge', {
    set: (value) => {
      bridge = value;
      if (typeof value?.postMessage !== 'function') return;
      while (messageQueue.length > 0) {
        const nextMessage = messageQueue.shift();
        sendMessage(nextMessage);
      }
    },
    get: () => bridge,
  });

  return (message) => {
    if (typeof window.CSSurveysJavascriptBridge?.postMessage === 'function') {
      sendMessage(message);
      return;
    }
    messageQueue.push(message);
  };
}

function getSdk(abortSignal) {
  const SDK_SITE_ID_PARAM = 'siteId';
  const SDK_LANGUAGE_PARAM = 'language';
  const SDK_REPLAY_LINK_PARAM = 'replay_link';
  const SDK_ENVIRONMENT_PARAM = 'env';

  const searchParams = new URLSearchParams(window.location.search);

  if (!searchParams.has(SDK_SITE_ID_PARAM)) {
    return undefined;
  }

  const notify = getNotifyBridge();
  registerSurveyEventHandler(notify, abortSignal);

  return {
    info: {
      getSiteId: () => Number(searchParams.get(SDK_SITE_ID_PARAM)),
      getDeviceLanguage: () => searchParams.get(SDK_LANGUAGE_PARAM) ?? undefined,
      getReplayLink: () => searchParams.get(SDK_REPLAY_LINK_PARAM) ?? undefined,
      getSdkEnvironment: () => searchParams.get(SDK_ENVIRONMENT_PARAM) ?? 'DEFAULT',
    },
    notify,
  };
}

async function getEnvironmentInfo(providedId, abortSignal) {
  const environment = getEnvironment('SURVEYS');
  const environmentSettings = SETTINGS[environment];
  const sdk = getSdk(abortSignal);

  if (sdk === undefined) {
    const { data: siteInfo } = await axios.get(
      `https://${environmentSettings.apiHost}/api/ask/v1/client/sites/offsite-survey-info/${providedId}`,
    );

    return {
      sdk: undefined,
      siteInfo,
      scriptSettings: SCRIPT_SETTINGS[environment],
      environmentSettings,
    };
  }

  const SDK_ENVIRONMENT_SETTINGS = {
    DEFAULT: {},
    SDK_QA: {
      askUrl: 'https://qa-mock-server.contentsquare.net/',
    },
  };

  return {
    sdk,
    siteInfo: { site_id: sdk.info.getSiteId(), enabled: true },
    scriptSettings: {
      ...SCRIPT_SETTINGS[environment],
      ...SDK_ENVIRONMENT_SETTINGS[sdk.info.getSdkEnvironment()],
    },
    environmentSettings,
  };
}

export const renderSurvey = async (abortSignal) => {
  const { pathname } = document.location;
  const providedId = pathname.replace('/', '');
  const embedContainer = document.getElementsByClassName('_hj-survey-embed-container')[0];

  document.addEventListener(
    'hj-embedded-survey-mismatch',
    () => {
      renderClosedSurvey(embedContainer);
    },
    { capture: false, signal: abortSignal },
  );

  // todo: at this point we don't have access to feature flags for site
  // change this host to refer to ask.hotjar.io entry once split from monolith is done
  // and usages of 'ask.separate_service' are removed
  try {
    const { environmentSettings, scriptSettings, siteInfo, sdk } = await getEnvironmentInfo(
      providedId,
      abortSignal,
    );

    if (siteInfo.enabled === false) {
      renderClosedSurvey(embedContainer, siteInfo.effective_show_branding);
      return;
    }

    embedContainer.setAttribute('data-survey-id', providedId);

    window.hj =
      window.hj ||
      // Don't change this to an arrow function. It minifies better this way.
      // eslint-disable-next-line func-names
      function () {
        // eslint-disable-next-line prefer-rest-params
        (window.hj.q = window.hj.q || []).push(arguments);
      };

    window.hj.sdk = sdk?.info;

    const hjid = siteInfo.site_id;
    const hjsv = 6;

    // eslint-disable-next-line no-underscore-dangle
    window._hjSettings = {
      hjid,
      hjsv,
      ...scriptSettings,
    };

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://${environmentSettings.clientScriptHost}/c/hotjar-${hjid}.js?sv=${hjsv}`;
    const head = document.getElementsByTagName('head')[0];
    head.appendChild(script);
  } catch (error) {
    if (error.response?.status === 409) {
      const environment = getEnvironment('SURVEYS');
      const safePath = /^\/[A-Za-z0-9_-]+$/.test(pathname) ? pathname : '/';
      window.location.href = `https://${getRedirectHost(environment)}${safePath}`;
      return;
    }

    renderClosedSurvey(embedContainer);
  }
};
